import "./styles.css"
import React, {useEffect, useState} from "react";
import {registerLocale, setDefaultLocale} from "react-datepicker"
import {useTranslation} from "react-i18next";
import de from 'date-fns/locale/de';
import Navigation from "../../container/navigation/Navigation";
import SiteTitle from "../../container/site-title/SiteTitle";
import {Navigate, Outlet, useLocation, useNavigate} from "react-router-dom";
import Footer from "../../molecules/footer/Footer";
import {RequireAuth, useFusionAuth} from "@fusionauth/react-sdk";
import {
    useGetAllUsersForAdminUserQuery,
    useGetUserInfoSelfQuery,
    useGetUsersAgenciesQuery,
    useGetUsersCompanyQuery
} from "../../redux/reducer/api";
import FullscreenSpinner from "../../molecules/fullscreen-spinner/FullscreenSpinner";
import ErrorPage from "../../screens/functional/error-page/ErrorPage";
import {PATH_STRINGS} from "../../routes/route-props";
import {
    companyAdminHasAcceptedTermsAndConditions,
    normalUserHasNotAcceptedTermsAndConditions
} from "../../utils/check/user";
import {useAppDispatch, useAppSelector} from "../../redux/reducer/store";
import {toast, ToastItem} from "react-toastify";
import ToastWithTextOnly from "../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
import {resetApiProps} from "../../redux/reducer/settings";
import Cookies from "js-cookie";
import {COOKIE_EXPIRY_NAME} from "../../constants/default-values";
import {cleanPathName} from "../../utils/string/common";
import clsx from "clsx";
import {validTravelComponentsOrJounrneyName} from "../forms/utils";

export const InstanceContext = React.createContext({
    disableButtonOnNotProduction: process.env.REACT_APP_INSTANCE === "local" ? false : process.env.REACT_APP_INSTANCE !== "prod",
})

const ProtectedLayout = () => {
    const {apiError, apiSuccess} = useAppSelector(state => state.settings)
    const {name} = useAppSelector(state => state.journey)

    const {t, i18n} = useTranslation()
    const {isAuthenticated, isLoading, login, logout} = useFusionAuth()
    const [heightClass, setHeightClass] = useState("protected-layout-container-with-responsive-height-adjustment")
    const dispatch = useAppDispatch()
    const location = useLocation();
    const navigate = useNavigate()

    const {
        data: userInfoResponse,
        isLoading: isLoadingUserInfoResponse,
        error: userInfoResponseError
    } = useGetUserInfoSelfQuery()
    useGetAllUsersForAdminUserQuery()
    const {error: agenciesResponseError} = useGetUsersAgenciesQuery()
    const {
        data: companyResponse,
        isLoading: isLoadingCompanyResponse,
        error: companyResponseError,
    } = useGetUsersCompanyQuery()

    const {pathname} = location
    const {resolvedLanguage} = i18n
    const cleanedPathName = cleanPathName(pathname)

    useEffect(() => {
        if (!isAuthenticated) login()
    }, [isAuthenticated, isLoading]);

    useEffect(() => {
        if (cleanedPathName === PATH_STRINGS.journey) {
            setHeightClass("protected-layout-container-without-responsive-height-adjustment")
        } else {
            setHeightClass("protected-layout-container-with-responsive-height-adjustment")
        }

        if (
            cleanedPathName.includes(PATH_STRINGS.addActivityToJourney) &&
            (
                !name ||
                (
                    name && !validTravelComponentsOrJounrneyName(name.toString())
                )
            )
        ) navigate(PATH_STRINGS.travelDatabase, {replace: true})
    }, [pathname]);

    useEffect(() => {
        const expiryToken = Cookies.get(COOKIE_EXPIRY_NAME)
        const intervalId = setInterval(() => {
            if (typeof expiryToken !== 'undefined') {
                const expiryDateInMilliseconds = Number.parseInt(expiryToken, 10)
                if (expiryDateInMilliseconds < Date.now()) {
                    // Dispatch action to show user the they are being logged out
                    // api.dispatch(...)
                    logout()
                }
            }
        }, 5000)
        return () => clearInterval(intervalId)
    }, []);

    useEffect(() => {
        if (apiError) {
            const {message, toastType} = apiError
            const toastId = toast(<ToastWithTextOnly
                    message={t(message)}/>,
                {progress: undefined, type: toastType}
            );
            const unsubscribe = toast.onChange((payload: ToastItem) => {
                switch (payload.status) {
                    case "added":
                        break;
                    case "updated":
                        break;
                    case "removed":
                        toastId && dispatch(resetApiProps("error"))
                        unsubscribe()
                        break;
                }
            })
        }
    }, [apiError]);

    useEffect(() => {
        if (apiSuccess) {
            const {message, toastType} = apiSuccess
            const toastId = toast(<ToastWithTextOnly
                    message={t(message)}/>,
                {progress: undefined, type: toastType}
            );
            const unsubscribe = toast.onChange((payload: ToastItem) => {
                switch (payload.status) {
                    case "added":
                        break;
                    case "updated":
                        break;
                    case "removed":
                        toastId && dispatch(resetApiProps("success"))
                        unsubscribe()
                        break;
                }
            })
        }
    }, [apiSuccess]);

    useEffect(() => {
        if (userInfoResponse) {
            const {language} = userInfoResponse
            language && language !== resolvedLanguage && i18n.changeLanguage(language)
        }
    }, [userInfoResponse]);

    useEffect(() => {
        // React CalenderPicker
        registerLocale("de", de)
    }, []);

    useEffect(() => {
        resolvedLanguage && setDefaultLocale(resolvedLanguage)
    }, [resolvedLanguage]);

    if (isLoadingUserInfoResponse || isLoadingCompanyResponse || agenciesResponseError) return <FullscreenSpinner
        showLogo={true}/>
    if (userInfoResponseError || companyResponseError || agenciesResponseError) return <ErrorPage
        error={userInfoResponseError}/>
    if (!companyAdminHasAcceptedTermsAndConditions(companyResponse, userInfoResponse)) return <Navigate
        to={PATH_STRINGS.termsAndConditionsAdmin}
        state={{showAcceptButton: true}}/>
    if (!normalUserHasNotAcceptedTermsAndConditions(userInfoResponse)) return <Navigate
        to={PATH_STRINGS.termsAndConditionsUser}/>
    if (userInfoResponse && !userInfoResponse.registrationComplete) return <Navigate to={PATH_STRINGS.register}/>
    return <RequireAuth>
        <Navigation/>
        <div className={"protected-layout-main"}>
            <SiteTitle/>
            <main
                className={clsx("protected-layout-container", heightClass)}>
                <Outlet/>
            </main>
        </div>
        <Footer/>
    </RequireAuth>
}

export default ProtectedLayout